import {
  CANONICAL_SIZE,
  GRID_OVERLAY_COLOR,
  SAFE_ZONE_INSET,
  SAFE_ZONE_OVERLAY_COLOR,
  SHAPE_HALF,
  SNAP_STEP,
} from '../constants'
import type { EmojiContent, IconConfig, ImageContent } from '../types'
import { computeImageBox, imageToDataUrl } from '../utils/image'
import { degreesToRadians } from '../utils/math'
import { walkShape } from '../utils/shape'
import type { TwemojiAsset } from '../utils/twemoji'

const CENTER = CANONICAL_SIZE / 2

export const EMOJI_FONT_FAMILY = '"Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", sans-serif'

const XML_ENTITIES: Record<string, string> = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }

const escapeXml = (value: string): string =>
  value.replace(/[<>&"']/g, (character) => XML_ENTITIES[character] ?? character)

const gradientDirection = (angleDegrees: number) => {
  const radians = degreesToRadians(angleDegrees)

  return { dx: Math.cos(radians), dy: Math.sin(radians) }
}

export interface IconRenderer {
  drawOuterFrame(corners: IconConfig['corners'], background: IconConfig['background']): void
  drawInnerFrame(corners: IconConfig['corners'], background: IconConfig['background'], inset: number): void
  drawImage(image: HTMLImageElement, content: ImageContent): void
  drawEmoji(content: EmojiContent, twemoji: TwemojiAsset | null): void
}

export class CanvasRenderer implements IconRenderer {
  readonly context: CanvasRenderingContext2D
  readonly canvasSize: number
  readonly scale: number

  constructor(context: CanvasRenderingContext2D, canvasSize: number) {
    this.context = context
    this.canvasSize = canvasSize
    this.scale = canvasSize / CANONICAL_SIZE
    context.clearRect(0, 0, canvasSize, canvasSize)
    context.save()
  }

  drawOuterFrame(corners: IconConfig['corners'], background: IconConfig['background']): void {
    const center = this.canvasSize / 2
    const half = SHAPE_HALF * this.scale

    this.path(center, half, corners)
    this.context.fillStyle = this.fill(background, center, half)
    this.context.fill()
    this.context.clip()
  }

  drawInnerFrame(corners: IconConfig['corners'], background: IconConfig['background'], inset: number): void {
    const center = this.canvasSize / 2
    const half = (SHAPE_HALF - inset) * this.scale

    this.context.save()
    this.path(center, half, corners)
    this.context.fillStyle = this.fill(background, center, half)
    this.context.fill()
    this.context.restore()
  }

  drawImage(image: HTMLImageElement, content: ImageContent): void {
    const center = this.canvasSize / 2
    const box = computeImageBox(image, content, center, center, this.scale)

    this.context.save()
    this.context.translate(box.centerX, box.centerY)
    this.context.rotate(degreesToRadians(content.rotation))
    this.context.drawImage(image, -box.w / 2, -box.h / 2, box.w, box.h)
    this.context.restore()
  }

  drawEmoji(content: EmojiContent, twemoji: TwemojiAsset | null): void {
    const center = this.canvasSize / 2
    const s = this.scale
    const cx = center + content.x * s
    const cy = center + content.y * s

    if (twemoji) {
      const size = content.size * s

      this.context.drawImage(twemoji.image, cx - size / 2, cy - size / 2, size, size)
    } else {
      this.context.font = `${content.size * s}px ${EMOJI_FONT_FAMILY}`
      this.context.textAlign = 'center'
      this.context.textBaseline = 'middle'
      this.context.fillText(content.emoji, cx, cy)
    }
  }

  drawGrid(): void {
    const { canvasSize, scale } = this
    const center = canvasSize / 2
    const step = SNAP_STEP * scale

    this.context.strokeStyle = GRID_OVERLAY_COLOR
    this.context.lineWidth = Math.max(0.5, scale * 0.25)
    this.context.beginPath()

    for (let x = center % step; x < canvasSize; x += step) {
      this.context.moveTo(x, 0)
      this.context.lineTo(x, canvasSize)
    }
    for (let y = center % step; y < canvasSize; y += step) {
      this.context.moveTo(0, y)
      this.context.lineTo(canvasSize, y)
    }
    this.context.stroke()
  }

  drawSafeZone(corners: IconConfig['corners']): void {
    const center = this.canvasSize / 2
    const scale = this.scale

    this.path(center, (SHAPE_HALF - SAFE_ZONE_INSET) * scale, corners)
    this.context.strokeStyle = SAFE_ZONE_OVERLAY_COLOR
    this.context.lineWidth = Math.max(1, scale * 0.4)
    this.context.setLineDash([scale * 2, scale * 2])
    this.context.stroke()
    this.context.setLineDash([])
  }

  finalize(): void {
    this.context.restore()
  }

  private path(center: number, half: number, corners: IconConfig['corners']): void {
    this.context.beginPath()
    walkShape(center, center, half, corners, {
      move: (x, y) => this.context.moveTo(x, y),
      line: (x, y) => this.context.lineTo(x, y),
      arc: (cx, cy, ex, ey, r) => this.context.arcTo(cx, cy, ex, ey, r),
    })
    this.context.closePath()
  }

  private fill(background: IconConfig['background'], center: number, half: number): string | CanvasGradient {
    if (background.type === 'solid') {
      return background.color
    } else if (background.type === 'linear') {
      const { dx, dy } = gradientDirection(background.angle)
      const g = this.context.createLinearGradient(
        center - dx * half,
        center - dy * half,
        center + dx * half,
        center + dy * half,
      )
      g.addColorStop(0, background.gradientStart)
      g.addColorStop(1, background.gradientEnd)
      return g
    } else {
      const g = this.context.createRadialGradient(center, center, 0, center, center, half)
      g.addColorStop(0, background.gradientStart)
      g.addColorStop(1, background.gradientEnd)
      return g
    }
  }
}

export class SvgRenderer implements IconRenderer {
  private readonly defs: string[] = []
  private readonly body: string[] = []
  private clipOpen = false
  private gradientCount = 0

  drawOuterFrame(corners: IconConfig['corners'], background: IconConfig['background']): void {
    const d = this.pathD(CENTER, SHAPE_HALF, corners)

    this.body.push(`<path d="${d}" fill="${this.fill(background, CENTER, CENTER, SHAPE_HALF)}"/>`)
    this.defs.push(`<clipPath id="clip"><path d="${d}"/></clipPath>`)
    this.body.push('<g clip-path="url(#clip)">')
    this.clipOpen = true
  }

  drawInnerFrame(corners: IconConfig['corners'], background: IconConfig['background'], inset: number): void {
    const half = SHAPE_HALF - inset
    const d = this.pathD(CENTER, half, corners)

    this.body.push(`<path d="${d}" fill="${this.fill(background, CENTER, CENTER, half)}"/>`)
  }

  drawImage(image: HTMLImageElement, content: ImageContent): void {
    const box = computeImageBox(image, content, CENTER, CENTER)
    const href = imageToDataUrl(image)
    const rotate = `rotate(${content.rotation} ${box.centerX} ${box.centerY})`

    this.body.push(
      `<image x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" href="${href}" transform="${rotate}"/>`,
    )
  }

  drawEmoji(content: EmojiContent, twemoji: TwemojiAsset | null): void {
    const x = CENTER + content.x
    const y = CENTER + content.y

    if (twemoji) {
      const size = content.size

      this.body.push(
        `<svg x="${x - size / 2}" y="${y - size / 2}" width="${size}" height="${size}" viewBox="0 0 36 36">${twemoji.inner}</svg>`,
      )
    } else {
      this.body.push(
        `<text x="${x}" y="${y}" font-size="${content.size}" text-anchor="middle" dominant-baseline="central" font-family='${EMOJI_FONT_FAMILY}'>${escapeXml(content.emoji)}</text>`,
      )
    }
  }

  toString(): string {
    if (this.clipOpen) {
      this.body.push('</g>')
      this.clipOpen = false
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${CANONICAL_SIZE}" height="${CANONICAL_SIZE}" viewBox="0 0 ${CANONICAL_SIZE} ${CANONICAL_SIZE}">
  <defs>${this.defs.join('')}</defs>
  ${this.body.join('')}
</svg>`
  }

  private pathD(center: number, half: number, corners: IconConfig['corners']): string {
    const out: string[] = []

    walkShape(center, center, half, corners, {
      move: (x, y) => out.push(`M${x} ${y}`),
      line: (x, y) => out.push(`L${x} ${y}`),
      arc: (_cx, _cy, ex, ey, r) => out.push(`A${r} ${r} 0 0 1 ${ex} ${ey}`),
      close: () => out.push('Z'),
    })

    return out.join(' ')
  }

  private fill(background: IconConfig['background'], cx: number, cy: number, half: number): string {
    if (background.type === 'solid') {
      return escapeXml(background.color)
    } else {
      const id = `bg${this.gradientCount++}`
      const stops = `<stop offset="0%" stop-color="${escapeXml(background.gradientStart)}"/><stop offset="100%" stop-color="${escapeXml(background.gradientEnd)}"/>`

      if (background.type === 'linear') {
        const { dx, dy } = gradientDirection(background.angle)

        this.defs.push(
          `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="${cx - dx * half}" y1="${cy - dy * half}" x2="${cx + dx * half}" y2="${cy + dy * half}">${stops}</linearGradient>`,
        )
      } else {
        this.defs.push(
          `<radialGradient id="${id}" gradientUnits="userSpaceOnUse" cx="${cx}" cy="${cy}" r="${half}">${stops}</radialGradient>`,
        )
      }

      return `url(#${id})`
    }
  }
}
